export type SidebarPolicyRole = 'schulleitung' | 'sekretariat' | 'klassenlehrer' | 'fachlehrer' | 'lehrer' | 'schueler';
export type NavigationPermissionState = 'allow' | 'inherit' | 'deny';

export type NavigationMenuKey =
  | 'dashboard'
  | 'unterricht.lernbuero'
  | 'unterricht.unterrichten'
  | 'unterricht.klassenbuch'
  | 'unterricht.klassenarbeit'
  | 'unterricht.display'
  | 'unterricht.anwesenheit'
  | 'unterricht.sitzordnung'
  | 'schueler.verwaltung'
  | 'schueler.statistik'
  | 'schueler.lernstand_automatik'
  | 'schueler.akten'
  | 'schueler.lerngruppen'
  | 'email.client'
  | 'dateien'
  | 'sonstiges.bausteine'
  | 'sonstiges.namen_lernen'
  | 'teacher.appointments'
  | 'sonstiges.zahlungen'
  | 'sonstiges.zahlungen.lehrer'
  | 'sonstiges.zahlungen.sekretariat'
  | 'sonstiges.zahlungen.schulleitung'
  | 'settings'
  | 'admin.curricula'
  | 'admin.geraetemanager'
  | 'admin.job_queue'
  | 'admin.kontostand'
  | 'admin.performance'
  | 'admin.stundenplan'
  | 'admin.showcase'
  | 'admin.license'
  | 'admin.acoustic_calibration'
  | 'student.dashboard'
  | 'student.lernplaene'
  | 'student.skillmap'
  | 'student.classwork'
  | 'student.klassenbuch'
  | 'student.dateien'
  | 'student.appointments';

export interface NavigationMenuDefinition {
  key: NavigationMenuKey;
  href: string;
  label: string;
  group: string;
  studentOnly?: boolean;
  /** Nur Berechtigungsmatrix / kein eigener Sidebar-Eintrag; bei gleichem href wie Basis-Menü nicht in Pfad→Key-Mapping */
  matrixOnly?: boolean;
}

export type NavigationPermissionMatrix = Partial<
  Record<NavigationMenuKey, Partial<Record<SidebarPolicyRole, NavigationPermissionState>>>
>;

type UserRoleLike =
  | string
  | {
      name?: string;
      role_name?: string;
    };

interface UserAssignmentLike {
  assignment_type?: string;
}

export interface NavigationPolicyUser {
  username?: string;
  role?: string;
  roles?: UserRoleLike[];
  allRoles?: UserRoleLike[];
  assignments?: UserAssignmentLike[];
}

export const NAVIGATION_PERMISSION_STORAGE_KEY = 'navigationPermissionsMatrix.v1';
export const NAVIGATION_PERMISSION_UPDATED_EVENT = 'navigation-permissions-updated';

export const NAVIGATION_POLICY_ROLES: Array<{ key: SidebarPolicyRole; label: string }> = [
  { key: 'schulleitung', label: 'Schulleitung' },
  { key: 'sekretariat', label: 'Sekretariat' },
  { key: 'klassenlehrer', label: 'Klassenlehrer' },
  { key: 'fachlehrer', label: 'Fachlehrer' },
  { key: 'lehrer', label: 'Lehrer' },
  { key: 'schueler', label: 'Schüler' }
];
const NAVIGATION_ROLE_PRIORITY: SidebarPolicyRole[] = [
  'schulleitung',
  'sekretariat',
  'klassenlehrer',
  'fachlehrer',
  'lehrer',
  'schueler'
];

export const NAVIGATION_MENU_DEFINITIONS: NavigationMenuDefinition[] = [
  { key: 'dashboard', href: '/dashboard', label: 'Dashboard', group: 'Dashboard' },
  { key: 'unterricht.lernbuero', href: '/lernbuero', label: 'Lernbüro', group: 'Unterricht' },
  { key: 'unterricht.unterrichten', href: '/live-unterricht', label: 'Unterrichten', group: 'Unterricht' },
  { key: 'unterricht.klassenbuch', href: '/klassenbuch', label: 'Klassenbuch', group: 'Unterricht' },
  { key: 'unterricht.klassenarbeit', href: '/unterrichten/klassenarbeit', label: 'Klassenarbeit', group: 'Unterricht' },
  { key: 'unterricht.display', href: '/display-steuerung', label: 'Display', group: 'Unterricht' },
  { key: 'unterricht.anwesenheit', href: '/attendance', label: 'Anwesenheit', group: 'Unterricht' },
  { key: 'unterricht.sitzordnung', href: '/sitzordnung', label: 'Sitzordnung', group: 'Unterricht' },
  { key: 'schueler.verwaltung', href: '/students', label: 'Schüler (alt)', group: 'Schüler' },
  { key: 'schueler.statistik', href: '/statistics', label: 'Statistik', group: 'Schüler' },
  { key: 'schueler.lernstand_automatik', href: '/student-assessments', label: 'Lernstand-Automatik', group: 'Schüler' },
  { key: 'schueler.akten', href: '/Akten', label: 'Akten', group: 'Schüler' },
  { key: 'schueler.lerngruppen', href: '/units', label: 'Lerngruppen', group: 'Schüler' },
  { key: 'email.client', href: '/E-Mail', label: 'E-Mail', group: 'Direkt' },
  { key: 'dateien', href: '/dateien', label: 'Dateien', group: 'Direkt' },
  { key: 'sonstiges.bausteine', href: '/building-blocks', label: 'Bausteine', group: 'Sonstiges' },
  { key: 'sonstiges.namen_lernen', href: '/namen-lernen', label: 'Namen lernen', group: 'Sonstiges' },
  { key: 'teacher.appointments', href: '/appointments', label: 'Terminbuchungen', group: 'Sonstiges' },
  { key: 'sonstiges.zahlungen', href: '/payments', label: 'Zahlungen', group: 'Sonstiges' },
  {
    key: 'sonstiges.zahlungen.lehrer',
    href: '/payments',
    label: 'Zahlungen: Lehrer (Kampagnen, Mahnungen)',
    group: 'Sonstiges',
    matrixOnly: true
  },
  {
    key: 'sonstiges.zahlungen.sekretariat',
    href: '/payments',
    label: 'Zahlungen: Sekretariat (Bank, Matching, Bar)',
    group: 'Sonstiges',
    matrixOnly: true
  },
  {
    key: 'sonstiges.zahlungen.schulleitung',
    href: '/payments',
    label: 'Zahlungen: Schulleitung (Übersicht, Freigaben)',
    group: 'Sonstiges',
    matrixOnly: true
  },
  { key: 'settings', href: '/settings', label: 'Einstellungen', group: 'Direkt' },
  { key: 'admin.curricula', href: '/curricula', label: 'Curricula', group: 'Admin' },
  { key: 'admin.geraetemanager', href: '/device-manager', label: 'Gerätemanager', group: 'Admin' },
  { key: 'admin.job_queue', href: '/admin/job-queue', label: 'Job-Queue', group: 'Admin' },
  { key: 'admin.kontostand', href: '/admin/kontostand', label: 'Rechnungsübersicht', group: 'Admin' },
  { key: 'admin.performance', href: '/performance', label: 'Performance', group: 'Admin' },
  { key: 'admin.stundenplan', href: '/timetables', label: 'Stundenpläne', group: 'Admin' },
  { key: 'admin.showcase', href: '/Showcase', label: 'UI Showcase', group: 'Admin' },
  { key: 'admin.license', href: '/license', label: 'Lizenz', group: 'Admin' },
  {
    key: 'admin.acoustic_calibration',
    href: '/admin/kalibrierung',
    label: 'Audio-Kalibrierung',
    group: 'Admin'
  },
  { key: 'student.dashboard', href: '/Schüler_Dashboard', label: 'Schüler Dashboard', group: 'Schüler (eigene Sicht)', studentOnly: true },
  { key: 'student.lernplaene', href: '/Meine Lernpläne', label: 'Meine Lernpläne', group: 'Schüler (eigene Sicht)', studentOnly: true },
  { key: 'student.skillmap', href: '/Skillmap', label: 'Skillmap', group: 'Schüler (eigene Sicht)', studentOnly: true },
  { key: 'student.classwork', href: '/student/classwork', label: 'Klassenarbeit', group: 'Schüler (eigene Sicht)', studentOnly: true },
  { key: 'student.klassenbuch', href: '/student/klassenbuch', label: 'Mein Klassenbuch', group: 'Schüler (eigene Sicht)', studentOnly: true },
  { key: 'student.dateien', href: '/dateien', label: 'Dateien', group: 'Schüler (eigene Sicht)', studentOnly: true },
  { key: 'student.appointments', href: '/appointments/student', label: 'Terminbuchungen', group: 'Schüler (eigene Sicht)', studentOnly: true }
];

const MENU_BY_PATH = new Map(
  NAVIGATION_MENU_DEFINITIONS.filter((item) => !item.matrixOnly).map((item) => [item.href, item.key])
);

const STUDENT_ONLY_KEYS = new Set<NavigationMenuKey>([
  'student.dashboard',
  'student.lernplaene',
  'student.skillmap',
  'student.classwork',
  'student.klassenbuch',
  'student.dateien',
  'student.appointments'
]);

const STUDENT_DEFAULT_ALLOWED = new Set<NavigationMenuKey>(STUDENT_ONLY_KEYS);
const ADMIN_ONLY_KEYS = new Set<NavigationMenuKey>([
  'unterricht.klassenarbeit',
  'admin.license',
  'admin.job_queue',
  'admin.performance',
  'admin.showcase',
  'admin.acoustic_calibration'
]);

const ADMIN_OR_ABTEILUNGSLEITER_KEYS = new Set<NavigationMenuKey>([
  'admin.curricula'
]);

const ADMIN_OR_SCHULLEITUNG_KEYS = new Set<NavigationMenuKey>(['admin.kontostand', 'admin.stundenplan']);
const ROLE_SYNONYMS: Record<string, string> = {
  administrator: 'admin',
  lehrer: 'teacher',
  sekretariat: 'sekretariat',
  schüler: 'student',
  schueler: 'student'
};

const TEACHER_ACCESS_ROLES = new Set<string>([
  'teacher',
  'sekretariat',
  'klassenlehrer',
  'abteilungsleiter',
  'schulleitung',
  'admin',
  'guest'
]);

function normalizeRoleName(roleValue: UserRoleLike | null | undefined): string {
  if (typeof roleValue === 'string') {
    const normalized = roleValue.trim().toLowerCase();
    return ROLE_SYNONYMS[normalized] || normalized;
  }
  if (roleValue && typeof roleValue === 'object') {
    const raw = String(roleValue.name || roleValue.role_name || '').trim().toLowerCase();
    return ROLE_SYNONYMS[raw] || raw;
  }
  return '';
}

export function getMenuKeyForPath(pathname: string): NavigationMenuKey | null {
  return MENU_BY_PATH.get(pathname) || null;
}

export function getNormalizedRoles(user: NavigationPolicyUser | null | undefined): Set<string> {
  const result = new Set<string>();
  if (!user) return result;

  const rawRoles = [...(user.allRoles || []), ...(user.roles || []), user.role || ''];
  rawRoles.forEach((role) => {
    const normalized = normalizeRoleName(role);
    if (normalized) result.add(normalized);
  });

  if (String(user.username || '').toLowerCase() === 'admin') {
    result.add('admin');
  }

  return result;
}

function hasSubjectAssignment(user: NavigationPolicyUser | null | undefined): boolean {
  const assignments = Array.isArray(user?.assignments) ? user?.assignments : [];
  return assignments.some((assignment) => String(assignment?.assignment_type || '').toLowerCase() === 'subject');
}

export function resolveMatrixRolesForUser(user: NavigationPolicyUser | null | undefined): SidebarPolicyRole[] {
  const normalizedRoles = getNormalizedRoles(user);
  const result = new Set<SidebarPolicyRole>();

  if (normalizedRoles.has('schulleitung')) result.add('schulleitung');
  if (normalizedRoles.has('sekretariat')) result.add('sekretariat');
  if (normalizedRoles.has('klassenlehrer')) result.add('klassenlehrer');
  if (normalizedRoles.has('teacher')) result.add('lehrer');
  if (normalizedRoles.has('student')) result.add('schueler');

  if (normalizedRoles.has('teacher') && hasSubjectAssignment(user)) {
    result.add('fachlehrer');
  }

  return Array.from(result);
}

export function isStudentOnlyUser(user: NavigationPolicyUser | null | undefined): boolean {
  const normalizedRoles = getNormalizedRoles(user);
  const hasStudent = normalizedRoles.has('student');
  const hasTeacherAccess = Array.from(normalizedRoles).some((role) => TEACHER_ACCESS_ROLES.has(role));
  return hasStudent && !hasTeacherAccess;
}

export function isAdminUser(user: NavigationPolicyUser | null | undefined): boolean {
  return getNormalizedRoles(user).has('admin');
}

function defaultAccess(menuKey: NavigationMenuKey, user: NavigationPolicyUser | null | undefined): boolean {
  const roles = getNormalizedRoles(user);
  const studentOnly = isStudentOnlyUser(user);

  if (studentOnly) {
    return STUDENT_DEFAULT_ALLOWED.has(menuKey);
  }

  if (STUDENT_ONLY_KEYS.has(menuKey)) {
    return false;
  }

  if (ADMIN_ONLY_KEYS.has(menuKey)) {
    return roles.has('admin');
  }
  if (ADMIN_OR_ABTEILUNGSLEITER_KEYS.has(menuKey)) {
    return roles.has('admin') || roles.has('abteilungsleiter');
  }
  if (menuKey === 'sonstiges.zahlungen') {
    return (
      roles.has('admin') ||
      roles.has('schulleitung') ||
      roles.has('sekretariat') ||
      roles.has('abteilungsleiter') ||
      roles.has('klassenlehrer') ||
      roles.has('teacher')
    );
  }
  if (menuKey === 'sonstiges.zahlungen.lehrer') {
    if (roles.has('sekretariat')) return false;
    if (roles.has('schulleitung') || roles.has('abteilungsleiter')) return false;
    return roles.has('teacher') || roles.has('klassenlehrer');
  }
  if (menuKey === 'sonstiges.zahlungen.sekretariat') {
    return roles.has('sekretariat');
  }
  if (menuKey === 'sonstiges.zahlungen.schulleitung') {
    return roles.has('schulleitung') || roles.has('abteilungsleiter');
  }
  if (ADMIN_OR_SCHULLEITUNG_KEYS.has(menuKey)) {
    return roles.has('admin') || roles.has('schulleitung');
  }

  return true;
}

export function canAccessMenuKey(params: {
  menuKey: NavigationMenuKey;
  user: NavigationPolicyUser | null | undefined;
  matrix?: NavigationPermissionMatrix | null;
}): boolean {
  const { menuKey, user, matrix } = params;
  // Student-only navigation must never leak into teacher/admin sidebars.
  if (STUDENT_ONLY_KEYS.has(menuKey) && !isStudentOnlyUser(user)) {
    return false;
  }
  if (isAdminUser(user)) return true;

  const fallback = defaultAccess(menuKey, user);
  const roleStates = matrix?.[menuKey];
  const matrixRoles = resolveMatrixRolesForUser(user);

  if (!roleStates || matrixRoles.length === 0) {
    return fallback;
  }

  const userRolesByPriority = NAVIGATION_ROLE_PRIORITY.filter((role) => matrixRoles.includes(role));
  for (const role of userRolesByPriority) {
    const state = roleStates[role] || 'inherit';
    if (state === 'allow') return true;
    if (state === 'deny') return false;
  }

  return fallback;
}

export function sanitizeNavigationPermissionMatrix(input: unknown): NavigationPermissionMatrix {
  const sanitized: NavigationPermissionMatrix = {};
  if (!input || typeof input !== 'object') {
    return sanitized;
  }

  const keys = new Set(NAVIGATION_MENU_DEFINITIONS.map((item) => item.key));
  const roleKeys = new Set<SidebarPolicyRole>([
    'schulleitung',
    'sekretariat',
    'klassenlehrer',
    'fachlehrer',
    'lehrer',
    'schueler'
  ]);

  Object.entries(input as Record<string, unknown>).forEach(([menuKey, roleMap]) => {
    if (!keys.has(menuKey as NavigationMenuKey) || !roleMap || typeof roleMap !== 'object') return;
    const cleanedRoleMap: Partial<Record<SidebarPolicyRole, NavigationPermissionState>> = {};
    Object.entries(roleMap as Record<string, unknown>).forEach(([role, state]) => {
      if (!roleKeys.has(role as SidebarPolicyRole)) return;
      if (state === 'allow' || state === 'inherit' || state === 'deny') {
        cleanedRoleMap[role as SidebarPolicyRole] = state;
      }
    });
    sanitized[menuKey as NavigationMenuKey] = cleanedRoleMap;
  });

  return sanitized;
}

export function readMatrixFromStorage(): NavigationPermissionMatrix {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(NAVIGATION_PERMISSION_STORAGE_KEY);
    if (!raw) return {};
    return sanitizeNavigationPermissionMatrix(JSON.parse(raw));
  } catch {
    return {};
  }
}

export function writeMatrixToStorage(matrix: NavigationPermissionMatrix): void {
  if (typeof window === 'undefined') return;
  try {
    const sanitized = sanitizeNavigationPermissionMatrix(matrix);
    window.localStorage.setItem(NAVIGATION_PERMISSION_STORAGE_KEY, JSON.stringify(sanitized));
    window.dispatchEvent(
      new CustomEvent(NAVIGATION_PERMISSION_UPDATED_EVENT, {
        detail: { matrix: sanitized }
      })
    );
  } catch {
    // ignore storage failures
  }
}
